import { supabase } from '../lib/supabase';

export interface UploadedImage {
  id: string;
  business_id: string;
  name: string;
  url: string;
  file_size?: number;
  mime_type?: string;
  width?: number;
  height?: number;
  uploaded_by?: string;
  created_at: string;
  updated_at: string;
}

class UploadedImageService {
  private bucketName = 'user-uploads';

  /**
   * Initialize the storage bucket if it doesn't exist
   */
  async initializeBucket() {
    try {
      // First, try to get files from the bucket to check if it exists
      const { error: checkError } = await supabase.storage
        .from(this.bucketName)
        .list('', { limit: 1 });
      
      // If bucket doesn't exist, create it
      if (checkError && checkError.message.includes('not found')) {
        console.log('Creating storage bucket:', this.bucketName);
        const { error: createError } = await supabase.storage.createBucket(this.bucketName, {
          public: true,
          fileSizeLimit: 5242880, // 5MB
          allowedMimeTypes: ['image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/webp']
        });
        
        if (createError) {
          console.error('Error creating bucket:', createError);
          // If creation fails, throw error to stop the upload process
          throw new Error(`Failed to create storage bucket: ${createError.message}`);
        } else {
          console.log('Storage bucket created successfully');
        }
      }
    } catch (error: any) {
      // If it's not a "bucket not found" error, rethrow it
      if (!error.message?.includes('not found')) {
        console.error('Error checking bucket:', error);
        throw error;
      }
    }
  }

  /**
   * Upload an image file to Supabase Storage and save metadata to database
   */
  async uploadImage(
    file: File,
    businessId: string,
    resourceId?: string
  ): Promise<UploadedImage | null> {
    try {
      console.log('Starting image upload for:', file.name);
      
      // Initialize bucket if needed
      await this.initializeBucket();

      // Generate unique file name
      const fileExt = file.name.split('.').pop();
      const fileName = `${businessId}/${Date.now()}_${Math.random().toString(36).substring(7)}.${fileExt}`;
      console.log('Generated filename:', fileName);

      // Upload to Supabase Storage
      console.log('Uploading to storage...');
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from(this.bucketName)
        .upload(fileName, file);

      if (uploadError) {
        console.error('Storage upload error:', uploadError);
        throw new Error(`Failed to upload file: ${uploadError.message}`);
      }

      console.log('Upload successful, getting public URL...');
      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from(this.bucketName)
        .getPublicUrl(fileName);
      
      console.log('Public URL:', publicUrl);

      // Get image dimensions (if it's an image)
      const dimensions = await this.getImageDimensions(file);

      // Save metadata to database
      console.log('Saving metadata to database...');
      const { data, error } = await supabase
        .from('user_uploaded_images')
        .insert({
          business_id: businessId,
          name: file.name,
          url: publicUrl,
          file_size: file.size,
          mime_type: file.type,
          width: dimensions?.width,
          height: dimensions?.height,
          uploaded_by: resourceId
        })
        .select()
        .single();

      if (error) {
        console.error('Database error:', error);
        // Try to delete the uploaded file
        console.log('Cleaning up uploaded file...');
        await supabase.storage.from(this.bucketName).remove([fileName]);
        throw new Error(`Failed to save image metadata: ${error.message}`);
      }

      console.log('Image uploaded successfully:', data);
      return data;
    } catch (error: any) {
      console.error('Upload failed:', error);
      alert(`Failed to upload image: ${error.message || 'Unknown error'}`);
      return null;
    }
  }

  /**
   * Get all uploaded images for a business
   */
  async getBusinessImages(businessId: string): Promise<UploadedImage[]> {
    try {
      const { data, error } = await supabase
        .from('user_uploaded_images')
        .select('*')
        .eq('business_id', businessId)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching images:', error);
        return [];
      }

      return data || [];
    } catch (error) {
      console.error('Error fetching images:', error);
      return [];
    }
  }

  /**
   * Delete an uploaded image
   */
  async deleteImage(imageId: string): Promise<boolean> {
    try {
      // Get image details first
      const { data: image, error: fetchError } = await supabase
        .from('user_uploaded_images')
        .select('url')
        .eq('id', imageId)
        .single();

      if (fetchError || !image) {
        console.error('Error fetching image:', fetchError);
        return false;
      }

      // Extract file path from URL
      const urlParts = image.url.split('/');
      const filePath = urlParts.slice(-2).join('/'); // Gets business_id/filename

      // Delete from storage
      const { error: storageError } = await supabase.storage
        .from(this.bucketName)
        .remove([filePath]);

      if (storageError) {
        console.error('Error deleting from storage:', storageError);
      }

      // Delete from database
      const { error: dbError } = await supabase
        .from('user_uploaded_images')
        .delete()
        .eq('id', imageId);

      if (dbError) {
        console.error('Error deleting from database:', dbError);
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error deleting image:', error);
      return false;
    }
  }

  /**
   * Get image dimensions from a file
   */
  private getImageDimensions(file: File): Promise<{ width: number; height: number } | null> {
    return new Promise((resolve) => {
      if (!file.type.startsWith('image/')) {
        resolve(null);
        return;
      }

      const img = new Image();
      img.onload = () => {
        resolve({ width: img.width, height: img.height });
      };
      img.onerror = () => {
        resolve(null);
      };
      img.src = URL.createObjectURL(file);
    });
  }

  /**
   * Upload multiple images
   */
  async uploadMultipleImages(
    files: File[],
    businessId: string,
    resourceId?: string,
    onProgress?: (uploaded: number, total: number) => void
  ): Promise<UploadedImage[]> {
    const uploadedImages: UploadedImage[] = [];
    
    for (let i = 0; i < files.length; i++) {
      const image = await this.uploadImage(files[i], businessId, resourceId);
      if (image) {
        uploadedImages.push(image);
      }
      if (onProgress) {
        onProgress(i + 1, files.length);
      }
    }
    
    return uploadedImages;
  }
}

export const uploadedImageService = new UploadedImageService();

